/** @format */

import {
  faBuilding,
  faChevronDown,
  faFilter,
  faSliders,
  faXmark,
} from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  InputAdornment,
  LinearProgress,
  Link,
  ListItemButton,
  Popover,
  Skeleton,
  Slider,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';

import {
  DOCS_OPTIONS,
  countActiveCriteria,
  criterionNumber,
  isRangeCriterion,
  isSingleBrandMode,
  normalizeText,
  rangeLabel,
  selectTypology,
  setCriterionRange,
  sortBrands,
  toggleBrand,
  toggleCriterion,
  visibleWithCount,
} from './catalogLogic';
import InfoDot from './InfoDot';

const BRAND_SEARCH_THRESHOLD = 10;

function SectionTitle({ children }) {
  return (
    <Typography
      component="h2"
      sx={{
        fontSize: 13,
        fontWeight: 700,
        textTransform: 'uppercase',
        color: 'text.secondary',
        mt: 3,
        mb: 1,
        px: 1.5,
      }}
    >
      {children}
    </Typography>
  );
}

SectionTitle.propTypes = { children: PropTypes.node.isRequired };

function Count({ value }) {
  return (
    <Box
      component="span"
      sx={{
        ml: 'auto',
        pl: 2,
        minWidth: 32,
        textAlign: 'right',
        fontSize: 13,
        color: 'text.secondary',
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {value}
    </Box>
  );
}

Count.propTypes = { value: PropTypes.node.isRequired };

function OptionRow({ selected, onClick, children, count, multiple }) {
  return (
    <ListItemButton
      selected={selected}
      onClick={onClick}
      aria-pressed={selected}
      sx={{
        minHeight: 44,
        borderRadius: 1,
        px: 1.5,
        gap: 1.5,
        fontSize: 15,
      }}
    >
      {multiple && (
        <Checkbox
          size="small"
          checked={selected}
          tabIndex={-1}
          disableRipple
          inputProps={{ 'aria-hidden': true }}
          sx={{ p: 0 }}
        />
      )}
      {children}
      {count !== null && <Count value={count} />}
    </ListItemButton>
  );
}

OptionRow.propTypes = {
  selected: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
  count: PropTypes.node,
  multiple: PropTypes.bool,
};

OptionRow.defaultProps = { count: null, multiple: false };

const FILTERS_SHAPE = PropTypes.shape({
  typology: PropTypes.string,
  subCategory: PropTypes.string,
  brands: PropTypes.arrayOf(PropTypes.number),
  criteria: PropTypes.shape({}),
  docs: PropTypes.arrayOf(PropTypes.string),
  showInactive: PropTypes.bool,
});

const VIEW_SHAPE = PropTypes.shape({
  typologyCounts: PropTypes.shape({}),
  subCategoryCounts: PropTypes.shape({}),
  subCategoryTotal: PropTypes.number,
  brandCounts: PropTypes.shape({}),
  criteriaCounts: PropTypes.shape({}),
  docsCounts: PropTypes.shape({}),
});

const CRITERIA_STATUS_SHAPE = PropTypes.shape({
  status: PropTypes.string,
  stale: PropTypes.bool,
});

// --- Left column: typologies and inactive products ---

function CatalogFilters({
  filters,
  onChange,
  typologies,
  view,
  ready,
  inactiveLoading,
}) {
  if (!ready) {
    return (
      <Box
        component="nav"
        aria-label="Filtres du catalogue"
        aria-busy="true"
        role="status"
      >
        <SectionTitle>Typologies</SectionTitle>
        {Array.from({ length: 12 }, (_, i) => (
          <Skeleton key={i} height={36} sx={{ mx: 1.5 }} />
        ))}
      </Box>
    );
  }

  const typologyCount = (slug) => view.typologyCounts[slug] || 0;
  const visibleTypologies = typologies.filter(
    (t) => t.slug === filters.typology || typologyCount(t.slug) > 0,
  );
  const allTypologiesCount = Object.values(view.typologyCounts).reduce(
    (a, b) => a + b,
    0,
  );

  return (
    <Box component="nav" aria-label="Filtres du catalogue">
      <Box role="group" aria-label="Typologies">
        <SectionTitle>Typologies</SectionTitle>
        <OptionRow
          selected={!filters.typology}
          onClick={() => onChange(selectTypology(filters, null))}
          count={allTypologiesCount}
        >
          Toutes
        </OptionRow>
        {visibleTypologies.map((t) => (
          <OptionRow
            key={t.slug}
            selected={filters.typology === t.slug}
            onClick={() =>
              onChange(
                selectTypology(filters, filters.typology === t.slug ? null : t),
              )
            }
            count={typologyCount(t.slug)}
          >
            {t.name}
          </OptionRow>
        ))}
      </Box>

      <Box sx={{ borderTop: 1, borderColor: 'divider', mt: 3, pt: 1 }}>
        <FormControlLabel
          sx={{ minHeight: 44, ml: 0, pl: 0.5, gap: 1 }}
          control={
            <Switch
              size="small"
              checked={filters.showInactive}
              onChange={(e) =>
                onChange({ ...filters, showInactive: e.target.checked })
              }
            />
          }
          label={
            <Box
              component="span"
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}
            >
              Fiches inactives
              {inactiveLoading && (
                <CircularProgress
                  size={14}
                  aria-label="Chargement des fiches inactives"
                />
              )}
            </Box>
          }
        />
      </Box>
    </Box>
  );
}

CatalogFilters.propTypes = {
  filters: FILTERS_SHAPE.isRequired,
  onChange: PropTypes.func.isRequired,
  typologies: PropTypes.arrayOf(
    PropTypes.shape({ slug: PropTypes.string, name: PropTypes.string }),
  ).isRequired,
  view: VIEW_SHAPE.isRequired,
  ready: PropTypes.bool.isRequired,
  inactiveLoading: PropTypes.bool.isRequired,
};

// --- Filter buttons above the cards, each with its drop-down menu ---

// Button in the style of the former catalog: outlined, filled once a value is chosen,
// with a cross to remove it.
function FilterMenu({
  name,
  icon,
  label,
  active,
  onClear,
  loading,
  disabled,
  children,
}) {
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const menuId = `catalog-filter-${name}`;
  return (
    <>
      <Box
        ref={anchorRef}
        sx={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}
      >
        <Button
          color="primary"
          disableElevation
          variant={active ? 'contained' : 'outlined'}
          disabled={disabled}
          onClick={() => setOpen(true)}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-controls={open ? menuId : undefined}
          startIcon={<FontAwesomeIcon icon={icon} />}
          endIcon={(() => {
            if (active) return null;
            if (loading) {
              return (
                <CircularProgress
                  size={12}
                  color="inherit"
                  aria-label="Chargement"
                />
              );
            }
            return (
              <FontAwesomeIcon icon={faChevronDown} style={{ fontSize: 12 }} />
            );
          })()}
          sx={{
            minHeight: 44,
            px: 2,
            pr: active ? 6 : 2,
            textTransform: 'none',
            fontSize: 15,
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </Button>
        {active && (
          <IconButton
            aria-label={`Retirer le filtre ${name}`}
            onClick={onClear}
            sx={{
              position: 'absolute',
              right: 0,
              top: 0,
              width: 44,
              height: 44,
              color: 'primary.contrastText',
            }}
          >
            <FontAwesomeIcon icon={faXmark} />
          </IconButton>
        )}
      </Box>
      <Popover
        id={menuId}
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            role: 'dialog',
            'aria-label': name,
            sx: {
              mt: 1,
              p: 1,
              minWidth: 280,
              maxWidth: 'calc(100vw - 32px)',
              maxHeight: '60vh',
              borderRadius: '16px',
            },
          },
        }}
      >
        {typeof children === 'function'
          ? children(() => setOpen(false))
          : children}
      </Popover>
    </>
  );
}

FilterMenu.propTypes = {
  name: PropTypes.string.isRequired,
  icon: PropTypes.shape({}).isRequired,
  label: PropTypes.node.isRequired,
  active: PropTypes.bool.isRequired,
  onClear: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]).isRequired,
};

FilterMenu.defaultProps = { loading: false, disabled: false };

function Hint({ children, color }) {
  return (
    <Typography variant="body2" color={color} sx={{ px: 1.5, py: 1 }}>
      {children}
    </Typography>
  );
}

Hint.propTypes = {
  children: PropTypes.node.isRequired,
  color: PropTypes.string,
};
Hint.defaultProps = { color: 'text.secondary' };

function BrandOptions({
  filters,
  onChange,
  brands,
  view,
  brandsStatus,
  onRetry,
}) {
  const [query, setQuery] = useState('');
  if (brandsStatus === 'error') {
    return (
      <Hint color="error">
        Marques indisponibles.{' '}
        <Link component="button" type="button" onClick={onRetry}>
          Réessayer
        </Link>
      </Hint>
    );
  }
  if (brandsStatus !== 'ready') {
    return (
      <Box role="status" aria-label="Chargement des marques">
        {Array.from({ length: 5 }, (_, i) => (
          <Skeleton key={i} height={36} sx={{ mx: 1.5 }} />
        ))}
      </Box>
    );
  }
  const visible = sortBrands(
    visibleWithCount(
      brands,
      (b) => b.id,
      view.brandCounts,
      (b) => filters.brands.includes(b.id),
    ),
    view.brandCounts,
  );
  const shown =
    visible.length > BRAND_SEARCH_THRESHOLD && normalizeText(query)
      ? visible.filter(
          (b) =>
            filters.brands.includes(b.id) ||
            normalizeText(b.name).includes(normalizeText(query)),
        )
      : visible;
  return (
    <>
      {isSingleBrandMode(filters) && (
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1 }}
        >
          <Typography variant="body2" color="text.secondary">
            Une marque à la fois
          </Typography>
          <InfoDot label="Pourquoi une seule marque ?">
            <Typography variant="body2">
              Sans typologie ni sous-catégorie choisie, on filtre une seule
              marque à la fois. Choisissez une typologie ou une sous-catégorie
              pour en cocher plusieurs.
            </Typography>
          </InfoDot>
        </Box>
      )}
      {visible.length > BRAND_SEARCH_THRESHOLD && (
        <TextField
          size="small"
          fullWidth
          placeholder="Chercher une marque"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          inputProps={{ 'aria-label': 'Chercher une marque' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 1 }}
        />
      )}
      {shown.map((b) => (
        <OptionRow
          key={b.id}
          multiple
          selected={filters.brands.includes(b.id)}
          onClick={() => onChange(toggleBrand(filters, b.id))}
          count={view.brandCounts[b.id] || 0}
        >
          <Box
            component="span"
            sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}
          >
            {b.image && (
              <Box
                component="img"
                src={b.image}
                alt=""
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
                sx={{
                  height: 20,
                  width: 48,
                  objectFit: 'contain',
                  flexShrink: 0,
                }}
              />
            )}
            {b.name}
          </Box>
        </OptionRow>
      ))}
      {!shown.length && <Hint>Aucune marque dans cette sélection.</Hint>}
    </>
  );
}

BrandOptions.propTypes = {
  filters: FILTERS_SHAPE.isRequired,
  onChange: PropTypes.func.isRequired,
  brands: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number }))
    .isRequired,
  view: VIEW_SHAPE.isRequired,
  brandsStatus: PropTypes.string.isRequired,
  onRetry: PropTypes.func.isRequired,
};

// Min / max of a numeric criterion: the slider snaps to the values in the catalog,
// the fields take any value.
function RangeOptions({ criterion, values, counts, range, onChange }) {
  const numbers = values
    .filter((v) => counts[v.key] > 0)
    .map((v) => criterionNumber(criterion, v.key))
    .filter((n) => n !== null);
  const lo = Math.min(...numbers);
  const hi = Math.max(...numbers);
  const current = [range?.min ?? lo, range?.max ?? hi];
  const [draft, setDraft] = useState(current);
  useEffect(() => setDraft(current), [range?.min, range?.max, lo, hi]);
  const commit = ([min, max]) =>
    onChange({ min: min <= lo ? null : min, max: max >= hi ? null : max });
  const format = (n) => rangeLabel(criterion, { min: n, max: n });
  const field = (bound, label) => (
    <TextField
      size="small"
      type="number"
      label={label}
      value={range?.[bound] ?? ''}
      placeholder={String(bound === 'min' ? lo : hi)}
      onChange={(e) => {
        const n = e.target.value === '' ? null : Number(e.target.value);
        if (n === null || Number.isFinite(n)) {
          onChange({ ...range, [bound]: n });
        }
      }}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">{criterion.unit}</InputAdornment>
        ),
        sx: { minHeight: 44 },
        inputProps: {
          'aria-label': `${criterion.label} ${label.toLowerCase()}`,
          inputMode: 'decimal',
        },
      }}
      sx={{ flex: 1, minWidth: 0 }}
    />
  );
  return (
    <Box sx={{ px: 1.5, pb: 1 }}>
      {numbers.length > 1 && lo < hi && (
        <Box sx={{ px: 1.5 }}>
          <Slider
            value={draft}
            min={lo}
            max={hi}
            step={null}
            marks={numbers.map((n) => ({ value: n }))}
            onChange={(_, v) => setDraft(v)}
            onChangeCommitted={(_, v) => commit(v)}
            valueLabelDisplay="auto"
            valueLabelFormat={format}
            getAriaLabel={(i) =>
              `${criterion.label} ${i === 0 ? 'minimum' : 'maximum'}`
            }
            getAriaValueText={format}
            disableSwap
          />
        </Box>
      )}
      <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
        {field('min', 'Min')}
        {field('max', 'Max')}
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        Dans le catalogue : {rangeLabel(criterion, { min: lo, max: hi })}
      </Typography>
    </Box>
  );
}

RangeOptions.propTypes = {
  criterion: PropTypes.shape({
    field: PropTypes.string,
    label: PropTypes.string,
    unit: PropTypes.string,
  }).isRequired,
  values: PropTypes.arrayOf(PropTypes.shape({ key: PropTypes.string }))
    .isRequired,
  counts: PropTypes.shape({}).isRequired,
  range: PropTypes.shape({ min: PropTypes.number, max: PropTypes.number }),
  onChange: PropTypes.func.isRequired,
};

RangeOptions.defaultProps = { range: null };

function CriteriaOptions({
  filters,
  onChange,
  view,
  criteria,
  criteriaStatus,
  onRetry,
}) {
  if (!filters.typology) {
    return (
      <Hint>
        Choisissez une typologie dans la colonne de gauche pour filtrer par
        caractéristiques.
      </Hint>
    );
  }
  if (criteriaStatus?.status === 'error') {
    return (
      <Hint color="error">
        Critères indisponibles.{' '}
        <Link component="button" type="button" onClick={onRetry}>
          Réessayer
        </Link>
      </Hint>
    );
  }
  if (criteriaStatus?.status !== 'complete') {
    return (
      <Box sx={{ px: 1.5, py: 1 }} role="status">
        <Typography variant="body2" color="text.secondary">
          Premier chargement des critères de cette typologie : ce processus peut
          être long.
        </Typography>
        <LinearProgress sx={{ mt: 1, borderRadius: 2 }} />
      </Box>
    );
  }
  const toggleDoc = (key) =>
    onChange({
      ...filters,
      docs: filters.docs.includes(key)
        ? filters.docs.filter((d) => d !== key)
        : [...filters.docs, key],
    });
  const groups = [
    ...criteria.map(({ criterion, values }) => {
      const counts = view.criteriaCounts[criterion.field] || {};
      if (isRangeCriterion(criterion)) {
        const range = filters.criteria[criterion.field] || null;
        const hasValues = values.some((v) => counts[v.key] > 0);
        return {
          key: criterion.field,
          label: criterion.label,
          range:
            hasValues || range ? { criterion, values, counts, range } : null,
          options: [],
        };
      }
      const selected = filters.criteria[criterion.field] || [];
      return {
        key: criterion.field,
        label: criterion.label,
        options: values
          .filter((v) => counts[v.key] > 0 || selected.includes(v.key))
          .map((v) => ({
            key: v.key,
            label: v.label,
            selected: selected.includes(v.key),
            toggle: () =>
              onChange(toggleCriterion(filters, criterion.field, v.key)),
          })),
      };
    }),
    {
      key: 'docs',
      label: 'Documents et photo',
      options: DOCS_OPTIONS.filter(
        ({ key }) => view.docsCounts[key] > 0 || filters.docs.includes(key),
      ).map(({ key, label }) => ({
        key,
        label,
        selected: filters.docs.includes(key),
        toggle: () => toggleDoc(key),
      })),
    },
  ].filter((g) => g.options.length || g.range);
  if (!groups.length) return <Hint>Aucun critère pour cette sélection.</Hint>;
  return groups.map((g) => (
    <Box key={g.key} role="group" aria-label={g.label} sx={{ mb: 1 }}>
      <Typography sx={{ fontSize: 14, fontWeight: 600, px: 1.5, pt: 1 }}>
        {g.label}
      </Typography>
      {g.range && (
        <RangeOptions
          {...g.range}
          onChange={(range) =>
            onChange(setCriterionRange(filters, g.key, range))
          }
        />
      )}
      {g.options.map((o) => (
        <OptionRow
          key={o.key}
          multiple
          selected={o.selected}
          onClick={o.toggle}
        >
          {o.label}
        </OptionRow>
      ))}
    </Box>
  ));
}

CriteriaOptions.propTypes = {
  filters: FILTERS_SHAPE.isRequired,
  onChange: PropTypes.func.isRequired,
  view: VIEW_SHAPE.isRequired,
  criteria: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  criteriaStatus: CRITERIA_STATUS_SHAPE,
  onRetry: PropTypes.func.isRequired,
};

CriteriaOptions.defaultProps = { criteriaStatus: undefined };

export function CatalogFilterBar({
  filters,
  onChange,
  subCategories,
  brands,
  view,
  criteria,
  ready,
  brandsStatus,
  onRetryBrands,
  criteriaStatus,
  onRetryCriteria,
}) {
  const subCategory = subCategories.find(
    (sc) => sc.slug === filters.subCategory,
  );
  const visibleSubCategories = visibleWithCount(
    subCategories,
    (sc) => sc.slug,
    view.subCategoryCounts,
    (sc) => sc.slug === filters.subCategory,
  );
  const criteriaCount =
    countActiveCriteria(filters.criteria) + filters.docs.length;
  let brandLabel = 'Marque';
  if (filters.brands.length === 1) {
    const brand = brands.find((b) => b.id === filters.brands[0]);
    brandLabel = `Marque : ${brand?.name || '—'}`;
  } else if (filters.brands.length > 1) {
    brandLabel = `Marques (${filters.brands.length})`;
  }

  return (
    <Box
      role="group"
      aria-label="Filtres"
      sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}
    >
      <FilterMenu
        name="Sous-catégorie"
        icon={faFilter}
        label={
          subCategory
            ? `Sous-catégorie : ${subCategory.name}`
            : 'Sous-catégorie'
        }
        active={Boolean(subCategory)}
        onClear={() => onChange({ ...filters, subCategory: null })}
        disabled={!ready}
      >
        {(close) =>
          [{ slug: null, name: 'Toutes' }, ...visibleSubCategories].map(
            (sc) => (
              <OptionRow
                key={sc.slug || 'all'}
                selected={filters.subCategory === sc.slug}
                onClick={() => {
                  onChange({ ...filters, subCategory: sc.slug });
                  close();
                }}
                count={
                  sc.slug
                    ? view.subCategoryCounts[sc.slug] || 0
                    : view.subCategoryTotal
                }
              >
                {sc.name}
              </OptionRow>
            ),
          )
        }
      </FilterMenu>
      <FilterMenu
        name="Critères"
        icon={faSliders}
        label={criteriaCount ? `Critères (${criteriaCount})` : 'Critères'}
        active={criteriaCount > 0}
        onClear={() => onChange({ ...filters, criteria: {}, docs: [] })}
        disabled={!ready}
        loading={
          Boolean(filters.typology) && criteriaStatus?.status === 'loading'
        }
      >
        <CriteriaOptions
          filters={filters}
          onChange={onChange}
          view={view}
          criteria={criteria}
          criteriaStatus={criteriaStatus}
          onRetry={onRetryCriteria}
        />
      </FilterMenu>
      <FilterMenu
        name="Marque"
        icon={faBuilding}
        label={brandLabel}
        active={filters.brands.length > 0}
        onClear={() => onChange({ ...filters, brands: [] })}
        disabled={!ready}
        loading={ready && brandsStatus === 'loading'}
      >
        <BrandOptions
          filters={filters}
          onChange={onChange}
          brands={brands}
          view={view}
          brandsStatus={brandsStatus}
          onRetry={onRetryBrands}
        />
      </FilterMenu>
    </Box>
  );
}

CatalogFilterBar.propTypes = {
  filters: FILTERS_SHAPE.isRequired,
  onChange: PropTypes.func.isRequired,
  subCategories: PropTypes.arrayOf(
    PropTypes.shape({ slug: PropTypes.string, name: PropTypes.string }),
  ).isRequired,
  brands: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number }))
    .isRequired,
  view: VIEW_SHAPE.isRequired,
  criteria: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  ready: PropTypes.bool.isRequired,
  brandsStatus: PropTypes.string.isRequired,
  onRetryBrands: PropTypes.func.isRequired,
  criteriaStatus: CRITERIA_STATUS_SHAPE,
  onRetryCriteria: PropTypes.func.isRequired,
};

CatalogFilterBar.defaultProps = { criteriaStatus: undefined };

export default CatalogFilters;
